import React, { useEffect, useRef, useCallback, useState } from 'react';
import { MapContainer, TileLayer, ZoomControl, LayersControl, Marker, Popup, useMap, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './BackgroundMap.css';
import L from 'leaflet';
import { LocalitaFeature } from '../../services/api';
import { MapLocationSet } from '../../types/mapTypes';
import LegendComponent from '../LegendaNumVol/LegendaNumVol';

// Interfaccia per lo stato della mappa
export interface MapState {
  center: { lat: number; lng: number };
  zoom: number;
  bounds?: {
    ne: { lat: number; lng: number };
    sw: { lat: number; lng: number };
  };
}

// Storage locale per gli stati della mappa tra diversi render
const mapStates: Record<string, MapState> = {};

// Componente per gestire gli eventi e lo stato della mappa
const MapController: React.FC<{
  pageId?: string;
  onMapReady?: (map: L.Map) => void;
  center?: [number, number];
  zoom?: number;
  onMapChange?: (center: [number, number], zoom: number) => void;
}> = ({ pageId, onMapReady, center, zoom, onMapChange }) => {
  const map = useMap();
  const initializedRef = useRef(false);
  const isMountedRef = useRef(true); // Traccia se il componente è ancora montato

  // Effetto per il controllo del montaggio/smontaggio
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Funzione per salvare lo stato della mappa - memoizzata con useCallback
  const saveMapState = useCallback(() => {
    try {
      // Verifica che la mappa sia disponibile e il componente sia ancora montato
      if (!isMountedRef.current || !map || !map.getContainer()) {
        return;
      }

      // Verifica se la mappa è effettivamente caricata
      if (typeof map.getCenter !== 'function') {
        console.warn('La mappa non è completamente inizializzata');
        return;
      }

      const center = map.getCenter();
      const zoom = map.getZoom();
      const bounds = map.getBounds();
      
      if (!center || !bounds) {
        console.warn('Impossibile salvare lo stato della mappa: valori mancanti');
        return;
      }
      
      const mapState: MapState = {
        center: { lat: center.lat, lng: center.lng },
        zoom,
        bounds: {
          ne: { lat: bounds.getNorthEast().lat, lng: bounds.getNorthEast().lng },
          sw: { lat: bounds.getSouthWest().lat, lng: bounds.getSouthWest().lng }
        }
      };
      
      // Usa il gestore esterno se fornito (per contesti specifici delle pagine)
      if (onMapChange) {
        onMapChange([center.lat, center.lng], zoom);
      }
      
      // Salva lo stato locale se pageId è fornito
      if (pageId) {
        mapStates[pageId] = mapState;
        console.log('Stato mappa salvato:', mapState);
      }
    } catch (err) {
      console.error('Errore durante il salvataggio dello stato della mappa:', err);
    }
  }, [map, pageId, onMapChange]);
  
  // Inizializzazione e cleanup
  useEffect(() => {
    if (!map || !map.getContainer()) {
      console.warn('Mappa non disponibile');
      return;
    }

    // Callback quando la mappa è pronta
    if (onMapReady) {
      onMapReady(map);
    }
    
    try {
      // Abilita le interazioni principali della mappa
      map.dragging.enable(); // Riattivo il trascinamento
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.keyboard.enable();
      
      // Mantieni attivo lo scrollWheelZoom
      map.scrollWheelZoom.enable();
      
      // Ripristina stato salvato o imposta valori iniziali
      if (pageId && mapStates[pageId] && !initializedRef.current) {
        const savedMap = mapStates[pageId];
        console.log('Ripristino dello stato della mappa:', savedMap);
        
        if (savedMap && savedMap.center) {
          map.setView([savedMap.center.lat, savedMap.center.lng], savedMap.zoom || zoom || 6);
        }
        
        initializedRef.current = true;
      } else if (center && zoom && !initializedRef.current) {
        map.setView(center, zoom);
        initializedRef.current = true;
      }
      
      // Registra handler per salvare lo stato quando cambia
      const handleMoveEnd = () => {
        if (isMountedRef.current) {
          saveMapState();
        }
      };
      
      const handleZoomEnd = () => {
        if (isMountedRef.current) {
          saveMapState();
        }
      };
      
      map.on('moveend', handleMoveEnd);
      map.on('zoomend', handleZoomEnd);
      
      return () => {
        // Verifica che la mappa sia ancora valida prima di rimuovere i listener
        if (map && map.getContainer()) {
          try {
            // Rimuove i listener della mappa
            map.off('moveend', handleMoveEnd);
            map.off('zoomend', handleZoomEnd);
            
            // Salva lo stato finale solo se il componente è ancora montato
            if (isMountedRef.current) {
              saveMapState();
            }
          } catch (err) {
            console.error('Errore durante la pulizia della mappa:', err);
          }
        }
      };
    } catch (err) {
      console.error('Errore durante l\'inizializzazione della mappa:', err);
      return () => {}; // Return empty cleanup function in case of error
    }
  }, [map, pageId, center, zoom, onMapReady, saveMapState]);

  return null;
};

// Funzione per ottenere il colore in base al numero di volontari
// Replica della funzione originale in support.js
const getColorByVolNum = (d: number, opacity: number = 1): string => {
  if (d > 6) {
    return `rgba(71, 67, 67, ${opacity})`; // #474343b5
  }
  
  switch (d) {
    case 6:
      return `rgba(71, 67, 67, ${opacity})`; // #474343b5
    case 5:
      return `rgba(157, 76, 235, ${opacity})`; // #9d4ceb
    case 4:
      return `rgba(235, 42, 42, ${opacity})`; // #eb2a2a
    case 3:
      return `rgba(253, 154, 4, ${opacity})`; // #fd9a04
    case 2:
      return `rgba(97, 243, 5, ${opacity})`; // #61f305
    case 1:
      return `rgba(0, 231, 255, ${opacity})`; // #00e7ff
    default:
      return `rgba(153, 153, 153, ${opacity})`; // grigio per 0 volontari
  }
};

interface BackgroundMapProps {
  onMapReady?: (map: L.Map) => void;
  center?: [number, number];
  zoom?: number;
  showControls?: boolean;
  pageId?: string; // ID della pagina per salvare lo stato
  onMapChange?: (center: [number, number], zoom: number) => void;
  features?: LocalitaFeature[];
  hoveredFeatureId?: string | number | null;
  boundsToFit?: MapLocationSet['bounds'] | null;
  onLocalitaAction?: (action: 'apri' | 'chiudi', feature: LocalitaFeature) => void;
}

// Stato di una località: 'open' = aperta, 'closed' = chiusa, 'new' = non visitata
const getLocState = (props: LocalitaFeature['properties']): 'open' | 'closed' | 'new' => {
  if (props.loc_ck_chiusa === 1 || (props.ora_chiusura_min && props.ora_chiusura_min > 0)) return 'closed';
  if (props.ora_apertura_min && props.ora_apertura_min > 0) return 'open';
  return 'new';
};

// Converte minuti dalla mezzanotte in HH:MM
const minutesToTime = (min: number): string => {
  if (!min || min === 0) return '--:--';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Gestore GeoJSON con supporto per hover
const GeoJSONWithHover: React.FC<{
  featuresProp: LocalitaFeature[];
  hoveredFeatureId?: string | number | null;
  onLocalitaAction?: (action: 'apri' | 'chiudi', feature: LocalitaFeature) => void;
}> = ({ featuresProp, hoveredFeatureId, onLocalitaAction }) => {
  const map = useMap();
  const geoJSONLayerRef = useRef<L.GeoJSON | null>(null);

  const ordinalMarkersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    if (geoJSONLayerRef.current) {
      geoJSONLayerRef.current.clearLayers();
    }
    ordinalMarkersRef.current.forEach(m => { try { map.removeLayer(m); } catch (_) {} });
    ordinalMarkersRef.current = [];

    if (featuresProp && featuresProp.length > 0) {
      // Crea un nuovo layer GeoJSON solo se ci sono features
      const newLayer = L.geoJSON(featuresProp as any, { // Cast ad any per superare problemi di tipo con react-leaflet V3 e L.GeoJSON
        style: (feature) => {
          const volNum = feature?.properties?.mob_vol_num || 0;
          const isHovered = hoveredFeatureId && feature?.id === hoveredFeatureId;
          const state = getLocState(feature?.properties as LocalitaFeature['properties']);

          let strokeColor = '#555';
          let strokeWeight = 2;
          let className = '';

          if (state === 'open') {
            strokeColor = '#f97316';
            strokeWeight = 3;
            className = 'loc-open-path';
          } else if (state === 'closed') {
            strokeColor = '#22c55e';
            strokeWeight = 3;
            className = 'loc-closed-path';
          }

          return {
            fillColor: getColorByVolNum(volNum, 0.6),
            weight: isHovered ? 4 : strokeWeight,
            opacity: 1,
            color: isHovered ? '#0056b3' : strokeColor,
            fillOpacity: isHovered ? 0.8 : 0.5,
            className,
          };
        },
        onEachFeature: (feature, layer) => {
          if (feature.properties) {
            const props = feature.properties as LocalitaFeature['properties'];
            const { mob_localita: loc_nome, mob_comune: loc_comune, mob_via_partenza: via_in, volantini, ora_apertura_min, ora_chiusura_min, mob_uscloc_id: id_uscloc } = props;
            const state = getLocState(props);

            const badgeHtml =
              state === 'open'
                ? '<span class="loc-popup-badge loc-popup-badge--open">● Aperta</span>'
                : state === 'closed'
                ? '<span class="loc-popup-badge loc-popup-badge--closed">✓ Chiusa</span>'
                : '<span class="loc-popup-badge loc-popup-badge--new">○ Non visitata</span>';

            const durataMin = (ora_apertura_min && ora_chiusura_min && ora_chiusura_min > ora_apertura_min)
              ? ora_chiusura_min - ora_apertura_min
              : null;

            const timesHtml =
              state !== 'new'
                ? `<div class="loc-popup-times">
                     <div class="loc-popup-times-col">
                       <span class="loc-popup-times-label">Aperto</span>
                       <span class="loc-popup-times-value">${minutesToTime(ora_apertura_min)}</span>
                     </div>
                     <div class="loc-popup-times-col">
                       <span class="loc-popup-times-label">Chiuso</span>
                       <span class="loc-popup-times-value">${ora_chiusura_min ? minutesToTime(ora_chiusura_min) : '--:--'}</span>
                     </div>
                     <div class="loc-popup-times-col">
                       <span class="loc-popup-times-label">Tempo</span>
                       <span class="loc-popup-times-value">${durataMin ? minutesToTime(durataMin) : '--:--'}</span>
                     </div>
                   </div>`
                : '';

            const chipsHtml =
              volantini && volantini.length > 0
                ? `<div class="loc-popup-chips">${volantini.map(v => {
                const cam = v.mob_usclocloc_ck_1_foto === 1 ? '<svg class="vol-chip-cam" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>' : '';
                const nome = v.vol_nome_produzione || v.mob_carico_vol_nome || '';
                const isCustom = v.vol_nome_produzione && v.vol_nome_produzione !== v.mob_carico_vol_nome;
                const qta = v.mob_usclocloc_ck_1_show_qta === 1 ? ` · ${v.mob_usclocloc_carico_vol}` : '';
                const label = isCustom ? `<span style="color:#dc2626;font-weight:bold">${nome}${qta}</span>` : `${nome}${qta}`;
                return `<span class="loc-popup-chip">${cam}${label}</span>`;
              }).join('')}</div>`
                : '';

            const canOpen = state === 'new';
            const canClose = state === 'open';

            const popupContent = `
              <div class="loc-popup">
                <div class="loc-popup-header">
                  ${badgeHtml}
                  <div class="loc-popup-title">${loc_nome || 'Località'}</div>
                  <div class="loc-popup-comune">${loc_comune || ''}</div>
                </div>
                ${timesHtml}
                ${via_in ? `<div class="loc-popup-via">📍 ${via_in}</div>` : ''}
                ${chipsHtml}
                <div class="loc-popup-actions">
                  <button class="loc-btn loc-btn-apri" data-uscloc="${id_uscloc}" ${!canOpen ? 'disabled' : ''}>
                    Apri zona
                  </button>
                  <button class="loc-btn loc-btn-chiudi" data-uscloc="${id_uscloc}" ${!canClose ? 'disabled' : ''}>
                    Chiudi zona
                  </button>
                </div>
              </div>
            `;
            layer.bindPopup(popupContent, { minWidth: 240 });

            layer.on({
              popupopen: (e: any) => {
                const container = e.popup.getElement() as HTMLElement | null;
                if (!container) return;
                const btnApri = container.querySelector('.loc-btn-apri:not([disabled])');
                if (btnApri) {
                  btnApri.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    if (onLocalitaAction) onLocalitaAction('apri', feature as LocalitaFeature);
                  });
                }
                const btnChiudi = container.querySelector('.loc-btn-chiudi:not([disabled])');
                if (btnChiudi) {
                  btnChiudi.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    if (onLocalitaAction) onLocalitaAction('chiudi', feature as LocalitaFeature);
                  });
                }
              },
              mouseover: function(e) {
                const l = e.target;
                l.setStyle({ weight: 4, fillOpacity: 0.7 });
                if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
                  l.bringToFront();
                }
              },
              mouseout: function(e) {
                if (geoJSONLayerRef.current) {
                  if (!(hoveredFeatureId && feature.id === hoveredFeatureId)) {
                    geoJSONLayerRef.current.resetStyle(e.target);
                  }
                }
              },
            });
          }
        }
      });
      newLayer.addTo(map);
      geoJSONLayerRef.current = newLayer;

      // Marker ordinale al centro di ogni poligono
      featuresProp.forEach(feature => {
        const props = feature.properties;
        if (props.mob_uscloc_order === undefined || props.mob_uscloc_order === null) return;
        let center: [number, number] | null = null;
        if (props.mob_localita_latlon) {
          const parts = props.mob_localita_latlon.split(',');
          if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);
            if (!isNaN(lat) && !isNaN(lng)) center = [lat, lng];
          }
        }
        if (!center) {
          try {
            const b = L.geoJSON(feature as any).getBounds();
            if (b.isValid()) { const c = b.getCenter(); center = [c.lat, c.lng]; }
          } catch (_) {}
        }
        if (!center) return;
        const bg = getColorByVolNum(props.mob_vol_num || 0, 0.75);
        const label = String(props.mob_uscloc_order + 1);
        const icon = L.divIcon({
          className: '',
          html: `<div style="width:20px;height:20px;border-radius:50%;background:${bg};color:#111;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,0.35);border:1.5px solid rgba(255,255,255,0.6);">${label}</div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });
        const marker = L.marker(center, { icon, interactive: false, zIndexOffset: 100 });
        marker.addTo(map);
        ordinalMarkersRef.current.push(marker);
      });
    } else if (geoJSONLayerRef.current) {
      // Se non ci sono featuresProp ma un layer esiste, puliscilo
      geoJSONLayerRef.current.clearLayers();
    }

    // Cleanup: rimuovi layer e marker ordinali
    return () => {
      ordinalMarkersRef.current.forEach(m => { try { map.removeLayer(m); } catch (_) {} });
      ordinalMarkersRef.current = [];
      if (geoJSONLayerRef.current) {
        map.removeLayer(geoJSONLayerRef.current);
        geoJSONLayerRef.current = null;
      }
    };
  }, [featuresProp, hoveredFeatureId, map]); // Dipendenze dell'effetto

  return null; // Questo componente non renderizza nulla direttamente
};

// Componente principale
const BackgroundMap: React.FC<BackgroundMapProps> = ({
  onMapReady,
  center = [42.9450, 13.8833],
  zoom = 6,
  showControls = true,
  pageId,
  onMapChange,
  features = [],
  hoveredFeatureId,
  boundsToFit,
  onLocalitaAction,
}) => {
  const mapRef = useRef<L.Map>(null);
  // Sospende il layer GeoJSON (pesante: poligoni reali dei comuni) durante flyToBounds.
  // Senza questo, Leaflet deve ridisegnare tutti i poligoni su canvas ad ogni frame
  // dell'animazione — compete con l'animazione stessa e produce scatti visibili.
  const [isFlying, setIsFlying] = useState(false);

  // Handler quando la mappa è pronta
  const handleMapReady = (map: L.Map) => {
    if (onMapReady) onMapReady(map);
    // Dopo il primo render il container ha la dimensione corretta:
    // invalidateSize() lo comunica a Leaflet, poi ripristiniamo il view.
    setTimeout(() => {
      try {
        if (map.getContainer()) {
          map.invalidateSize();
          if (!boundsToFit) {
            map.setView(initialCenter, initialZoom);
          }
        }
      } catch (_) {}
    }, 200);
  };
  
  // Se abbiamo uno stato salvato per questa pagina, utilizzalo come center e zoom iniziali
  let initialCenter = center;
  let initialZoom = zoom;
  
  if (pageId && mapStates[pageId]) {
    const savedMap = mapStates[pageId];
    if (savedMap && savedMap.center) {
      initialCenter = [savedMap.center.lat, savedMap.center.lng] as [number, number];
      initialZoom = savedMap.zoom || zoom;
    }
  }

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !boundsToFit) return;

    const leafletBounds = L.latLngBounds(
      [boundsToFit.south, boundsToFit.west],
      [boundsToFit.north, boundsToFit.east]
    );
    if (!leafletBounds.isValid()) return;

    setIsFlying(true);
    const handleSettled = () => {
      setIsFlying(false);
      map.off('moveend', handleSettled);
    };
    map.once('moveend', handleSettled);
    map.flyToBounds(leafletBounds, { padding: [0, 0], animate: true, duration: 2.2 });

    return () => {
      map.off('moveend', handleSettled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsToFit]);

  // Vista di default sulla home (nessuna uscita selezionata) — indipendente dal volo sopra.
  useEffect(() => {
    if (mapRef.current && !boundsToFit && (!features || features.length === 0) && pageId === 'home-logged-in') {
      mapRef.current.flyTo(center, zoom, { animate: true, duration: 2.2 });
    }
  }, [boundsToFit, features, center, zoom, pageId]);

  return (
    <div className="background-map-container">
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        zoomControl={false}
        dragging={true} // Attiva il dragging anche inizialmente
        touchZoom={true}
        doubleClickZoom={true}
        scrollWheelZoom={true} // Abilita lo zoom con la rotella del mouse
        className="map-background"
        ref={mapRef}
        attributionControl={false}
        preferCanvas={true}
      >
        <MapController 
          pageId={pageId}
          onMapReady={handleMapReady}
          center={center}
          zoom={zoom}
          onMapChange={onMapChange}
        />
        
        {showControls && <ZoomControl position="topright" />}
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Mappa">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              // updateWhenZooming=false: durante flyToBounds Leaflet scala via CSS le tile
              // esistenti (fluido) invece di richiederne di nuove ad ogni livello di zoom
              // attraversato — il caricamento continuo di tile durante l'animazione era
              // un'altra causa degli scatti, indipendente dal layer poligoni.
              updateWhenZooming={false}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
              updateWhenZooming={false}
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        
        {features.length > 0 && !isFlying && (
          <GeoJSONWithHover
            featuresProp={features}
            hoveredFeatureId={hoveredFeatureId}
            onLocalitaAction={onLocalitaAction}
          />
        )}
        

      </MapContainer>
      <LegendComponent getColorStyleForNumVol={getColorByVolNum} />
    </div>
  );
};

export default BackgroundMap; 